/* ============================================================== */
/*   Print list ของหน้า Document (browser only)                     */
/* ============================================================== */
/*
 * ทำตาม react-lib/apps/common/CardPrintList.tsx โดยใช้ helper และ controller เดิมของ V3
 *   print_api = "/pdfmake" -> สร้าง PDF ฝั่ง browser จากแบบฟอร์มใน CardPrintListFormDataHelper
 *   print_api อื่น        -> เรียก API ของ BE ซึ่งคืน PDF, task_id หรือสั่งพิมพ์ที่ print server
 *
 * เก็บ PDF ใน state ของ card ไม่เก็บใน sequence เพราะเป็นไฟล์ชั่วคราวที่มีขนาดใหญ่
 * ใช้ dynamic import เพราะ helper ลากแบบฟอร์มทั้งหมดมาด้วย
 */
import axios from "axios";
import moment from "moment";

import CONFIG from "config/config";
import { printURL } from "react-lib/utils/printURL";
import { beStringToAdString, beToAd } from "react-lib/utils/dateUtils";

import type { ClinicFormItem } from "./documentModelV4";

const HOST = `${CONFIG.API_HOST}`;
const PDFMAKE_PRINT_API = "/pdfmake";

export type PrintListController = Record<string, (...args: any[]) => Promise<any[]>>;

export type PrintFormContext = {
  controller: PrintListController;
  patientInfo: Record<string, any>;
  patientId?: number;
  encounterId?: number;
  encounterNumber?: string;
  emrId?: number;
  masterOptions?: Record<string, any>;
  masterData?: Record<string, any>;
  selectedDivision?: Record<string, any>;
};

export type PrintResult =
  | { kind: "file"; url: string }
  | { kind: "printed" }
  | { kind: "task"; taskId: string; progress?: string }
  | { kind: "warning"; message: string }
  | { kind: "error"; message: string };

const errorText = (error: unknown, fallback: string): string => {
  if (typeof error === "string" && error) return error;
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === "string" && message ? message : fallback;
};

/** แปลงผลของ print API ตาม handleGetDocumentResult ของ V3 */
export const printResultOf = (data: any, preview: boolean): PrintResult => {
  if (preview && data?.success && data?.pdf_b64data?.length > 0) {
    return { kind: "file", url: `data:application/pdf;base64,${data.pdf_b64data}` };
  }
  if (!preview && data?.success) return { kind: "printed" };
  if (data?.warning) return { kind: "warning", message: String(data.warning) };
  if (data?.task_id) return { kind: "task", taskId: String(data.task_id) };
  return { kind: "error", message: errorText(data?.error, "สร้างเอกสารไม่สำเร็จ") };
};

const callPrintApi = async (
  form: ClinicFormItem,
  context: PrintFormContext,
  preview: boolean,
): Promise<PrintResult> => {
  const [data, error] = await context.controller.getPrintAPI({
    printAPI: HOST + form.printApi,
    patientId: context.patientId,
    encounterId: context.encounterId,
    jasperModule: form.jasperModule,
    jasperPath: form.jasperPath,
    pdf: preview,
    confirm: false,
    reason: "",
  });
  if (error || !data) return { kind: "error", message: errorText(error, "สร้างเอกสารไม่สำเร็จ") };
  return printResultOf(data, preview);
};

const blobToUrl = (blob: Blob): string => URL.createObjectURL(blob);

/** สร้างไฟล์จากแบบฟอร์ม pdfmake ตาม getPrintPDFMake ของ V3 คืน URL ที่ iframe และ printURL ใช้ได้ */
const buildPdfmakeUrl = async (form: ClinicFormItem, context: PrintFormContext): Promise<string | null> => {
  const [helper, common, pdfMakeModule] = await Promise.all([
    import("react-lib/apps/common/CardPrintListFormDataHelper"),
    import("react-lib/apps/common/CardPrintListCommon"),
    import("react-lib/appcon/common/pdfMake"),
  ]);
  const getPdfMake = pdfMakeModule.default;

  const now = moment();
  const buddhistYear = String(now.year() + 543);
  const formattedDate = now.format("DD/MM/YYYY".replace("YYYY", buddhistYear));
  const printedDateTh = now.clone().locale("th").format("DD/MM/YYYY, HH:mm".replace("YYYY", buddhistYear));
  const printedDateEn = now.format("DD/MM/YYYY, HH:mm");
  const { patientInfo } = context;

  const preparedData = {
    prepCreatedDateTh: common.GetSlideDate(printedDateTh),
    prepCreatedDateEn: common.GetSlideDate(printedDateEn, "en"),
    prepSignedDateTh: printedDateTh.split(", "),
    prepSignedDateEn: printedDateEn.split(", "),
    prepPatientName: await common.GetPatientNameInForm(patientInfo),
    prepPatientAge: common.CalculateAge(beToAd(patientInfo.birthdate), beToAd(formattedDate)),
    prepPatientBirthDateEn: beStringToAdString(patientInfo.birthdate),
    prepPatientAddress: common.GetFormatAddress(patientInfo),
    prepDoctorName: await common.GetDoctorNameInForm(context.controller as any, context.encounterId as number),
    haveHeader: CONFIG.ENABLE_HEADER_PRINTLIST,
    printer: "-",
    showService: true,
    serviceDate: formattedDate,
    serviceTime: now.format("HH:mm"),
  };

  const code = String(form.code ?? "");
  const formData: any = await helper.GetFormData(code, {
    props: {
      controller: context.controller,
      patientInfo,
      selectedPatient: patientInfo,
      patientId: context.patientId,
      encounterId: context.encounterId,
      encounterNumber: context.encounterNumber,
      masterOptions: context.masterOptions,
      masterData: context.masterData,
      selectedDivision: context.selectedDivision,
      isEnableHeader: CONFIG.ENABLE_HEADER_PRINTLIST,
    },
    preparedData: preparedData as any,
    emrId: context.emrId as any,
    encounterId: context.encounterId as any,
    extras: form.source as any,
  });

  let doc: any;
  if (formData && helper.CUSTOM_PDF_LIST.includes(code)) {
    doc = await helper.CreateMergedForm(helper.FORM_PDFDATA_MAPPING[code], formData.data);
  } else if (formData) {
    const merged =
      code === "package_information"
        ? { ...formData, data: (formData.data || []).flat() }
        : formData;
    const items = code === "package_information" ? merged.data : merged.data?.items;
    doc = await helper.CreateWithHeaderForm(
      helper.FORM_PDFDATA_MAPPING[code],
      merged,
      code === "package_information" ? false : items?.haveHeader,
      CONFIG.COMPANY,
      {
        createdFormDate: merged.language === "th" ? printedDateTh : printedDateEn,
        formName: merged.formName,
        patientInfo: items,
        ...(items?.haveFooter ? { footer: items.haveFooter } : {}),
      } as any,
    );
  } else {
    const pathFile = form.jasperPath || helper.FALLBACK_PDF_PATHS[code];
    if (!pathFile) return null;
    doc = { pathFile };
  }

  if (typeof doc === "string") return doc;
  if (doc?.blobRaw) return blobToUrl(doc.blobRaw);
  if (doc?.base64String) return `data:application/pdf;base64,${doc.base64String}`;
  if (doc?.pathFile) {
    const response = await axios.get(doc.pathFile, { responseType: "arraybuffer" });
    return blobToUrl(new Blob([response.data], { type: "application/pdf" }));
  }

  const pdf = (await getPdfMake(true)).createPdf(doc);
  const blob = await new Promise<Blob>((resolve) => {
    pdf.getBlob((value: Blob) => resolve(value));
  });
  return blobToUrl(blob);
};

const NO_FILE_RESULT: PrintResult = {
  kind: "error",
  message: "แบบฟอร์มนี้ยังไม่มีเอกสารสำหรับแสดง",
};

/** สร้างไฟล์สำหรับดูตัวอย่าง */
export const previewPrintForm = async (
  form: ClinicFormItem,
  context: PrintFormContext,
): Promise<PrintResult> => {
  try {
    if (form.printApi !== PDFMAKE_PRINT_API) return await callPrintApi(form, context, true);
    const url = await buildPdfmakeUrl(form, context);
    return url === null ? NO_FILE_RESULT : { kind: "file", url };
  } catch (error: unknown) {
    return { kind: "error", message: errorText(error, "สร้างเอกสารไม่สำเร็จ") };
  }
};

/** สั่งพิมพ์: แบบ print_api พิมพ์ที่ print server ส่วน pdfmake เปิดหน้าต่างพิมพ์ของ browser */
export const printPrintForm = async (
  form: ClinicFormItem,
  context: PrintFormContext,
  previewUrl?: string,
): Promise<PrintResult> => {
  try {
    if (form.printApi !== PDFMAKE_PRINT_API) return await callPrintApi(form, context, false);
    const url = previewUrl ?? (await buildPdfmakeUrl(form, context));
    if (url === null) return NO_FILE_RESULT;
    printURL(url);
    return { kind: "file", url };
  } catch (error: unknown) {
    return { kind: "error", message: errorText(error, "พิมพ์เอกสารไม่สำเร็จ") };
  }
};

/** ตรวจงานสร้างเอกสารที่ BE ทำแบบ background ตาม handleTaskTrackingStatus ของ V3 */
export const checkPrintTask = async (
  controller: PrintListController,
  taskId: string,
): Promise<PrintResult> => {
  try {
    const [data, error] = await controller.getTaskTrackingResult({ taskId });
    if (error || !data) return { kind: "error", message: errorText(error, "ตรวจสถานะเอกสารไม่สำเร็จ") };

    if (data.status === "COMPLETED") {
      const [result, resultError] = await controller.getTaskTrackingResult({ taskId });
      if (resultError || !result) {
        return { kind: "error", message: errorText(resultError, "อ่านเอกสารไม่สำเร็จ") };
      }
      /* แถวที่ยังมี status แปลว่ายังไม่ได้ไฟล์ ให้รอรอบถัดไป */
      return result.status === undefined ? printResultOf(result, true) : { kind: "task", taskId };
    }
    if (data.status === "FAILED") return { kind: "error", message: `สร้างเอกสารไม่สำเร็จ (task ${taskId})` };
    if (data.status === "CANCELED") {
      return { kind: "error", message: errorText(data.current_progress_description, "ยกเลิกการสร้างเอกสารแล้ว") };
    }
    if (data.status === "IN_PROGRESS") {
      const progress = data.current_progress ?? data.progress;
      return { kind: "task", taskId, progress: progress === undefined ? undefined : `${progress}%` };
    }
    return printResultOf(data, true);
  } catch (error: unknown) {
    return { kind: "error", message: errorText(error, "ตรวจสถานะเอกสารไม่สำเร็จ") };
  }
};

export const cancelPrintTask = (controller: PrintListController, taskId: string): Promise<any[]> =>
  controller.getTaskTrackingResult({ taskId, action: "CANCEL" });

export type PrintEncounterOption = { id: number; text: string };

/** Encounter ทั้งหมดของผู้ป่วยรายนี้ ค้นด้วย HN เสมอเพื่อไม่ให้เลือกของคนอื่นได้ */
export const loadPrintEncounters = async (
  controller: PrintListController,
  patientHn: string,
): Promise<PrintEncounterOption[]> => {
  const [data, error] = await controller.getEncounterSearch({ patientHn });
  if (error) throw new Error(errorText(error, "โหลดรายการ Encounter ไม่สำเร็จ"));
  const items: any[] = Array.isArray(data?.items) ? data.items : [];
  return items
    .filter((item) => Number.isFinite(Number(item?.id)))
    .map((item) => ({ id: Number(item.id), text: String(item.number_date ?? item.number ?? item.id) }));
};

/** ข้อมูลของ Encounter ที่เลือก เลข EMR ต้องตามมาด้วยเพราะแบบฟอร์มอ่าน Dx และหัตถการจาก EMR */
export const resolvePrintEncounter = async (
  controller: PrintListController,
  encounterId: number,
): Promise<{ encounterId: number; encounterNumber: string; doctorName: string; emrId?: number }> => {
  const [detail, detailError] = await controller.getEncounterSearchDetail({ encounterId });
  if (detailError || !detail) throw new Error(errorText(detailError, "อ่านข้อมูล Encounter ไม่สำเร็จ"));
  const [encounter, encounterError] = await controller.getEncounter(encounterId);
  if (encounterError || !encounter) throw new Error(errorText(encounterError, "อ่านข้อมูล Encounter ไม่สำเร็จ"));
  const emrId = Number(encounter.medical_record);
  return {
    encounterId,
    encounterNumber: String(detail.number ?? ""),
    doctorName: String(detail.doctor_name ?? ""),
    emrId: Number.isFinite(emrId) && emrId > 0 ? emrId : undefined,
  };
};

export const downloadPrintFile = (url: string, name: string): void => {
  const link = document.createElement("a");
  link.href = url;
  link.download = `${name}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
};
